import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
    Table, Button, Space, Modal, Form, Input,
    InputNumber, Select, Popconfirm, message, Typography, Drawer, Skeleton
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BellOutlined, SearchOutlined, IdcardOutlined } from '@ant-design/icons';
import DatePicker from '_helpers/DatePicker';
import dayjs from '_helpers/datetime';
import { useTuitionActions } from '_actions/tuition.actions';
import { useNotificationActions } from '_actions/notification.actions';
import { normalizeVietnamese } from '_helpers/normalize';
import { useCapabilities } from '_helpers/capabilities';
import { useHistory, useLocation } from 'react-router-dom';

const { Text } = Typography;
const { Option } = Select;

// Design-guide §4.5 tag-tone mapping: quota status, not payment status.
const QUOTA_STATUS_TONE = { 'Còn buổi': 'ok', 'Cần gia hạn': 'bad', 'Sắp hết buổi': 'warn', 'Học thử': 'info', 'Chưa có gói': 'neutral' };

// Plain §4.5 tinted tag — no icon, no pill radius, no bold.
function StatusTag({ tone, children }) {
    return <span className={`att2-tag t-${tone}`}>{children}</span>;
}

function deriveQuotaStatus(row) {
    const status = row.student_ref?.enrollment_status || 'active';
    if (status === 'trial') return 'Học thử';
    if (!row.total_quota) return 'Chưa có gói';
    if (status === 'active' && row.lessons_remaining <= 0) return 'Cần gia hạn';
    if (status === 'active' && row.lessons_remaining <= 10) return 'Sắp hết buổi';
    return 'Còn buổi';
}

function formatMoney(amount) {
    return amount.toLocaleString('vi-VN') + ' đ';
}

// ── Change-log (audit) rendering helpers ─────────────────────────────────────
const AUDIT_ACTION_VI = { create: 'Tạo mới', update: 'Chỉnh sửa', delete: 'Xóa' };
const AUDIT_ENTITY_VI = { record: 'Khoản nộp', package: 'Gói học' };
const AUDIT_FIELD_VI = {
    amount: 'Số tiền', payment_date: 'Ngày nộp', payment_method: 'Hình thức', notes: 'Ghi chú',
    lesson_quota: 'Số buổi', price: 'Giá gói',
};
function fmtAuditVal(field, val) {
    if (val === undefined || val === null || val === '') return '—';
    if (field === 'amount' || field === 'price') return Number(val).toLocaleString('vi-VN') + ' đ';
    if (field === 'payment_date') return dayjs(val).format('DD/MM/YYYY');
    return String(val);
}
// One-line human summary of a change: created fields, deleted fields, or old→new for edits.
function describeChange(row) {
    const fields = row.entity === 'package'
        ? ['lesson_quota', 'price', 'notes']
        : ['amount', 'payment_date', 'payment_method', 'notes'];
    const present = (o) => fields.filter(f => o && o[f] !== undefined && o[f] !== null && o[f] !== '');
    if (row.action === 'create') {
        const a = row.after || {};
        return present(a).map(f => `${AUDIT_FIELD_VI[f]}: ${fmtAuditVal(f, a[f])}`).join(', ') || '—';
    }
    if (row.action === 'delete') {
        const b = row.before || {};
        return 'Đã xóa — ' + (present(b).map(f => `${AUDIT_FIELD_VI[f]}: ${fmtAuditVal(f, b[f])}`).join(', ') || '—');
    }
    const b = row.before || {}, a = row.after || {};
    const changed = fields.filter(f => String(b[f] ?? '') !== String(a[f] ?? ''));
    if (!changed.length) return 'Không đổi';
    return changed.map(f => `${AUDIT_FIELD_VI[f]}: ${fmtAuditVal(f, b[f])} → ${fmtAuditVal(f, a[f])}`).join(', ');
}

// ── Drawer detail: payment history + add/edit/delete ─────────────────────────
function TuitionDetailDrawer({ visible, studentRow, onClose, onRefresh, isMobile }) {
    const studentData = studentRow || {};
    const tuitionActions = useTuitionActions();
    const { canManageTuition, canViewTuitionAudit } = useCapabilities();
    const [modalVisible, setModalVisible]   = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [saving, setSaving]               = useState(false);
    const [records, setRecords]             = useState(null);
    const [packages, setPackages]           = useState(null);
    const [summary, setSummary]             = useState(null);
    const [loadingRecords, setLoadingRecords] = useState(false);
    const [loadError, setLoadError]         = useState('');
    const loadRunRef = useRef(0);
    const [form] = Form.useForm();

    // ── package (Gói học) modal state ─────────────────────────────────────────
    const [pkgModalVisible, setPkgModalVisible] = useState(false);
    const [editingPackage, setEditingPackage]   = useState(null);
    const [savingPkg, setSavingPkg]             = useState(false);
    const [pkgForm] = Form.useForm();

    // ── Change-log (audit) modal state — managers only ────────────────────────
    const [auditVisible, setAuditVisible] = useState(false);
    const [auditRows, setAuditRows]       = useState([]);
    const [auditLoading, setAuditLoading] = useState(false);
    async function openAudit() {
        setAuditVisible(true);
        setAuditLoading(true);
        const rows = await tuitionActions.getTuitionAudit(studentData.student_ref?.student_id);
        setAuditRows(rows);
        setAuditLoading(false);
    }

    const loadRecords = useCallback(async () => {
        const sid = studentRow?.student_ref?.student_id;
        if (!visible || !sid) return;
        const runId = ++loadRunRef.current;
        setLoadingRecords(true);
        setLoadError('');
        setSummary({
            total_quota:       studentRow.total_quota,
            total_price:       studentRow.total_price,
            total_paid:        studentRow.total_paid,
            still_owe:         studentRow.still_owe,
            lessons_used:      studentRow.lessons_used,
            lessons_remaining: studentRow.lessons_remaining,
            warning:           studentRow.warning,
        });
        const detail = await tuitionActions.getStudentTuition(sid);
        if (runId !== loadRunRef.current) return;
        if (!detail) {
            setRecords([]);
            setPackages([]);
            setLoadError('Không tải được dữ liệu học phí của học sinh này');
            setLoadingRecords(false);
            return;
        }
        setRecords(detail.records || []);
        setPackages(detail.packages || []);
        setSummary(detail ? {
            total_quota:       detail.total_quota,
            total_price:       detail.total_price,
            total_paid:        detail.total_paid,
            still_owe:         detail.still_owe,
            lessons_used:      detail.lessons_used,
            lessons_remaining: detail.lessons_remaining,
            warning:           detail.warning,
        } : null);
        setLoadingRecords(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, studentRow?.student_ref?.student_id]);

    useEffect(() => {
        if (visible) loadRecords();
        else {
            loadRunRef.current += 1;
            setRecords(null);
            setPackages(null);
            setSummary(null);
            setLoadError('');
        }
    }, [visible, loadRecords]);

    function openAdd() {
        setEditingRecord(null);
        form.resetFields();
        setModalVisible(true);
    }

    function openEdit(record) {
        setEditingRecord(record);
        form.setFieldsValue({
            payment_date:   dayjs(record.payment_date),
            amount:         record.amount,
            payment_method: record.payment_method,
            notes:          record.notes || '',
        });
        setModalVisible(true);
    }

    async function handleSave() {
        let values;
        try { values = await form.validateFields(); } catch { return; }
        setSaving(true);
        try {
            const payload = {
                payment_date:   values.payment_date.format('YYYY-MM-DD'),
                amount:         values.amount,
                payment_method: values.payment_method,
                notes:          values.notes || '',
            };
            let json;
            if (editingRecord) {
                json = await tuitionActions.updateTuitionRecord(editingRecord._id, payload);
            } else {
                json = await tuitionActions.addTuitionRecord({
                    student_id: studentData.student_ref.student_id,
                    ...payload,
                });
            }
            if (json.status === 'Success') {
                message.success(editingRecord ? 'Đã cập nhật' : 'Đã thêm khoản nộp');
                setModalVisible(false);
                await loadRecords();
                await onRefresh?.();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Có lỗi xảy ra');
            }
        } catch (e) {
            message.error('Có lỗi xảy ra');
        }
        setSaving(false);
    }

    async function handleDelete(recordId) {
        const json = await tuitionActions.deleteTuitionRecord(recordId);
        if (json.status === 'Success') {
            message.success('Đã xóa');
            await loadRecords();
            await onRefresh?.();
        } else {
            message.error('Xóa thất bại');
        }
    }

    // ── package (Gói học) handlers ────────────────────────────────────────────
    function openAddPackage() {
        setEditingPackage(null);
        pkgForm.resetFields();
        setPkgModalVisible(true);
    }

    function openEditPackage(pkg) {
        setEditingPackage(pkg);
        pkgForm.setFieldsValue({ lesson_quota: pkg.lesson_quota, price: pkg.price || 0, notes: pkg.notes || '' });
        setPkgModalVisible(true);
    }

    async function handleSavePackage() {
        let values;
        try { values = await pkgForm.validateFields(); } catch { return; }
        setSavingPkg(true);
        try {
            const payload = { lesson_quota: values.lesson_quota, price: values.price ?? 0, notes: values.notes || '' };
            const json = editingPackage
                ? await tuitionActions.updateTuitionPackage(editingPackage._id, payload)
                : await tuitionActions.addTuitionPackage({ student_id: studentData.student_ref.student_id, ...payload });
            if (json.status === 'Success') {
                message.success(editingPackage ? 'Đã cập nhật gói học' : 'Đã thêm gói học');
                setPkgModalVisible(false);
                await loadRecords();
                await onRefresh?.();
            } else {
                message.error(typeof json.message === 'string' ? json.message : 'Có lỗi xảy ra');
            }
        } catch (e) {
            message.error('Có lỗi xảy ra');
        }
        setSavingPkg(false);
    }

    async function handleDeletePackage(packageId) {
        const json = await tuitionActions.deleteTuitionPackage(packageId);
        if (json.status === 'Success') {
            message.success('Đã xóa gói học');
            await loadRecords();
            await onRefresh?.();
        } else {
            message.error('Xóa thất bại');
        }
    }

    const pkgCols = [
        {
            title: 'Số buổi',
            dataIndex: 'lesson_quota',
            key: 'lesson_quota',
            width: 90,
            render: v => <Text strong>{v}</Text>,
        },
        {
            title: 'Giá gói',
            dataIndex: 'price',
            key: 'price',
            width: 130,
            align: 'right',
            render: v => v > 0 ? <Text strong>{formatMoney(v)}</Text> : <Text type="secondary">—</Text>,
        },
        {
            title: 'Ghi chú',
            dataIndex: 'notes',
            key: 'notes',
            render: n => n || <Text type="secondary">—</Text>,
        },
        {
            title: 'Ngày tạo',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 120,
            render: d => d ? dayjs(d).format('DD/MM/YYYY') : <Text type="secondary">—</Text>,
        },
        // Manage actions column only for managers/headmasters — teachers are view-only.
        ...(canManageTuition ? [{
            title: '',
            key: 'actions',
            width: 90,
            render: (_, pkg) => (
                <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEditPackage(pkg)} />
                    <Popconfirm
                        title="Xóa gói học này?"
                        onConfirm={() => handleDeletePackage(pkg._id)}
                        okText="Xóa" cancelText="Hủy"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        }] : []),
    ];

    const histCols = [
        {
            title: 'Ngày nộp',
            dataIndex: 'payment_date',
            key: 'payment_date',
            width: 120,
            render: d => dayjs(d).format('DD/MM/YYYY'),
        },
        {
            title: 'Số tiền',
            dataIndex: 'amount',
            key: 'amount',
            width: 140,
            render: v => <Text strong style={{ color: 'var(--ok)' }}>{formatMoney(v)}</Text>,
        },
        {
            title: 'Hình thức',
            dataIndex: 'payment_method',
            key: 'payment_method',
            width: 130,
            render: m => (
                <StatusTag tone={m === 'Tiền mặt' ? 'info' : 'neutral'}>{m}</StatusTag>
            ),
        },
        {
            title: 'Ghi chú',
            dataIndex: 'notes',
            key: 'notes',
            render: n => n || <Text type="secondary">—</Text>,
        },
        // Manage actions column only for managers/headmasters — teachers are view-only.
        ...(canManageTuition ? [{
            title: '',
            key: 'actions',
            width: 90,
            render: (_, rec) => (
                <Space size={4}>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(rec)}
                    />
                    <Popconfirm
                        title="Xóa khoản nộp này?"
                        onConfirm={() => handleDelete(rec._id)}
                        okText="Xóa" cancelText="Hủy"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        }] : []),
    ];

    return (
        <Drawer
            className="tt-drawer"
            title={
                <div>
                    <div className="tt-drawer-title">{studentData.student_ref?.name || 'Chi tiết học phí'}</div>
                    <div className="tt-drawer-sub">{studentData.student_ref?.student_id || ''}</div>
                </div>
            }
            placement="right"
            visible={visible}
            onClose={onClose}
            destroyOnClose
            width={isMobile ? '100vw' : 760}
        >
        {loadingRecords && !summary ? (
            <Skeleton active paragraph={{ rows: 8 }} />
        ) : loadError ? (
            <div className="tt-drawer-error">
                <div>{loadError}</div>
                <Button size="small" onClick={loadRecords}>Thử lại</Button>
            </div>
        ) : (
        <div className="tt-detail tt-detail-drawer">
            {canViewTuitionAudit && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                    <Button size="small" onClick={openAudit}>Lịch sử thay đổi</Button>
                </div>
            )}
            {/* ── Gói học (lesson packages) ───────────────────────────────────── */}
            <div className="tt-detail-head">
                <Space size={8}>
                    <span className="tt-detail-title">Gói học</span>
                    {summary && summary.total_quota > 0 && (
                        <span className="tt-quota-note">
                            Còn {Math.max(0, summary.lessons_remaining)}/{summary.total_quota} buổi
                            {' '}(đã dùng {summary.lessons_used})
                        </span>
                    )}
                    {summary && summary.warning && <StatusTag tone="warn">Sắp hết</StatusTag>}
                </Space>
                {canManageTuition && (
                    <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openAddPackage}
                    >
                        Thêm gói học
                    </Button>
                )}
            </div>

            <Table
                columns={pkgCols}
                dataSource={(packages || []).map(p => ({ ...p, key: p._id }))}
                loading={loadingRecords}
                pagination={false}
                size="small"
                style={{ marginBottom: 16 }}
                locale={{ emptyText: 'Chưa có gói học nào' }}
            />

            {/* ── Tổng kết tiền (price / paid / còn nợ) ───────────────────────── */}
            {summary && (
                <div className="stat-strip tt-summary-strip">
                    <span className="stat"><span className="l">Tổng giá gói</span><span className="v">{formatMoney(summary.total_price || 0)}</span></span>
                    <span className="stat"><span className="l">Đã nộp</span><span className="v tone-ok">{formatMoney(summary.total_paid || 0)}</span></span>
                    <span className="stat">
                        <span className="l">Còn nợ</span>
                        {(summary.still_owe || 0) > 0
                            ? <span className="v tone-bad">{formatMoney(summary.still_owe)}</span>
                            : (summary.still_owe || 0) < 0
                                ? <span className="v tone-ok">Dư {formatMoney(-summary.still_owe)}</span>
                                : <span className="v tone-ok">Đã đủ</span>}
                    </span>
                </div>
            )}

            {/* ── Lịch sử đóng học phí (payment history) ──────────────────────── */}
            <div className="tt-detail-head">
                <span className="tt-detail-title">Lịch sử đóng học phí — {studentData.student_ref?.name}</span>
                {canManageTuition && (
                    <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openAdd}
                    >
                        Thêm khoản nộp
                    </Button>
                )}
            </div>

            <Table
                columns={histCols}
                dataSource={(records || []).map(r => ({ ...r, key: r._id }))}
                loading={loadingRecords}
                pagination={false}
                size="small"
                locale={{ emptyText: 'Chưa có khoản nộp nào' }}
            />

            {/* ── Lịch sử thay đổi (append-only change-log — managers only, read-only) ── */}
            <Modal
                title={`Lịch sử thay đổi học phí — ${studentData.student_ref?.name || ''}`}
                visible={auditVisible}
                onCancel={() => setAuditVisible(false)}
                footer={null}
                width={760}
            >
                <Table
                    loading={auditLoading}
                    dataSource={(auditRows || []).map(r => ({ ...r, key: r._id }))}
                    pagination={{ pageSize: 8, hideOnSinglePage: true }}
                    size="small"
                    locale={{ emptyText: 'Chưa có thay đổi nào' }}
                    columns={[
                        { title: 'Thời gian', dataIndex: 'createdAt', width: 120,
                          render: v => dayjs(v).format('DD/MM/YY HH:mm') },
                        { title: 'Người thực hiện', width: 130,
                          render: (_, r) => r.actor_ref?.name || '—' },
                        { title: 'Thao tác', width: 130,
                          render: (_, r) => `${AUDIT_ACTION_VI[r.action] || r.action} ${AUDIT_ENTITY_VI[r.entity] || ''}` },
                        { title: 'Chi tiết', render: (_, r) => describeChange(r) },
                    ]}
                />
            </Modal>

            <Modal
                title={editingRecord ? 'Chỉnh sửa khoản nộp' : 'Thêm khoản nộp học phí'}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleSave}
                confirmLoading={saving}
                okText={editingRecord ? 'Lưu' : 'Thêm'}
                cancelText="Hủy"
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="payment_date"
                        label="Ngày nộp"
                        rules={[{ required: true, message: 'Chọn ngày nộp' }]}
                    >
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>
                    <Form.Item
                        name="amount"
                        label="Số tiền (VND)"
                        rules={[{ required: true, message: 'Nhập số tiền' }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            step={100000}
                            formatter={v => v ? v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                            parser={v => v.replace(/,/g, '')}
                            placeholder="VD: 1,500,000"
                        />
                    </Form.Item>
                    <Form.Item
                        name="payment_method"
                        label="Hình thức nộp"
                        rules={[{ required: true, message: 'Chọn hình thức' }]}
                    >
                        <Select placeholder="Chọn hình thức">
                            <Option value="Tiền mặt">Tiền mặt</Option>
                            <Option value="Chuyển khoản">Chuyển khoản</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="notes" label="Ghi chú (tùy chọn)">
                        <Input.TextArea rows={2} placeholder="Ghi chú thêm..." />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={editingPackage ? 'Chỉnh sửa gói học' : 'Thêm gói học'}
                visible={pkgModalVisible}
                onCancel={() => setPkgModalVisible(false)}
                onOk={handleSavePackage}
                confirmLoading={savingPkg}
                okText={editingPackage ? 'Lưu' : 'Thêm'}
                cancelText="Hủy"
            >
                <Form form={pkgForm} layout="vertical">
                    <Form.Item
                        name="lesson_quota"
                        label="Số buổi học"
                        rules={[{ required: true, message: 'Nhập số buổi học' }]}
                    >
                        <InputNumber style={{ width: '100%' }} min={1} placeholder="VD: 12" />
                    </Form.Item>
                    <Form.Item
                        name="price"
                        label="Giá gói (VND)"
                        tooltip="Tổng giá của gói học này. Còn nợ = tổng giá các gói − tổng đã nộp."
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            step={100000}
                            formatter={v => v ? v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                            parser={v => v.replace(/,/g, '')}
                            placeholder="VD: 5,000,000"
                        />
                    </Form.Item>
                    <Form.Item name="notes" label="Ghi chú (tùy chọn)">
                        <Input.TextArea rows={2} placeholder="Ghi chú thêm..." />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
        )}
        </Drawer>
    );
}

// ── Main table ────────────────────────────────────────────────────────────────
export { TuitionTable };

function TuitionTable() {
    const tuitionActions = useTuitionActions();
    const notificationActions = useNotificationActions();
    const history = useHistory();
    const location = useLocation();
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [data,       setData]       = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [sortedInfo, setSortedInfo] = useState({ columnKey: 'name', order: 'ascend' });
    const [isMobile,   setIsMobile]   = useState(window.innerWidth <= 768);
    const [remindingId, setRemindingId] = useState(null);
    const [filterMode, setFilterMode] = useState(params.get('status') || 'all');
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
    const [selectedRow, setSelectedRow] = useState(null);

    async function sendTuitionReminder(row) {
        const sid = row.student_ref?.student_id;
        if (!sid) return;
        setRemindingId(sid);
        try {
            const res = await notificationActions.sendTuitionReminder(sid);
            if (res.status === 'Success') message.success('Đã gửi nhắc học phí');
            else message.error(res.message || 'Gửi thất bại');
        } catch (e) {
            message.error('Gửi thất bại');
        } finally {
            setRemindingId(null);
        }
    }

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        const result = await tuitionActions.getAllTuition();
        setData(Array.isArray(result) ? result : []);
        setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { loadData(); }, [loadData]);
    useEffect(() => {
        const status = params.get('status');
        if (status && status !== filterMode) setFilterMode(status);
        const studentId = params.get('studentId');
        if (studentId && data.length && !selectedRow) {
            const found = data.find(r =>
                r.student_ref?.student_id === studentId ||
                String(r.student_ref?._id) === String(studentId)
            );
            if (found) setSelectedRow(found);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, data.length]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, current: 1 }));
        const q = new URLSearchParams(location.search);
        if (filterMode === 'all') q.delete('status'); else q.set('status', filterMode);
        const next = `${location.pathname}${q.toString() ? `?${q.toString()}` : ''}`;
        if (`${location.pathname}${location.search}` !== next) history.replace(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterMode, search]);

    const totalPaid   = data.reduce((s, d) => s + (d.total_paid || 0), 0);
    // Only sum positive balances — overpaid (credit) students shouldn't reduce the
    // center's outstanding-debt total.
    const totalOwed   = data.reduce((s, d) => s + Math.max(0, d.still_owe || 0), 0);
    const countOwed = data.filter(d => (d.still_owe || 0) > 0).length;
    const countRenewal = data.filter(d => deriveQuotaStatus(d) === 'Cần gia hạn').length;
    const countTrial = data.filter(d => d.student_ref?.enrollment_status === 'trial').length;

    const filteredData = useMemo(() => {
        const q = normalizeVietnamese(search.trim());
        return data.filter(row => {
            const quotaStatus = deriveQuotaStatus(row);
            const enrollment = row.student_ref?.enrollment_status || 'active';
            const matchesMode =
                filterMode === 'all' ||
                (filterMode === 'owed' && (row.still_owe || 0) > 0) ||
                (filterMode === 'renew' && quotaStatus === 'Cần gia hạn') ||
                (filterMode === 'low' && quotaStatus === 'Sắp hết buổi') ||
                (filterMode === 'trial' && enrollment === 'trial') ||
                (filterMode === 'settled' && (row.total_price || 0) > 0 && (row.still_owe || 0) <= 0);
            if (!matchesMode) return false;
            if (!q) return true;
            const name = normalizeVietnamese(row.student_ref?.name || '');
            const sid = (row.student_ref?.student_id || '').toLowerCase();
            return name.includes(q) || sid.includes(q);
        });
    }, [data, filterMode, search]);

    const quickFilters = [
        { key: 'all', label: 'Tất cả', count: data.length },
        { key: 'owed', label: 'Còn nợ', count: countOwed },
        { key: 'renew', label: 'Cần gia hạn', count: countRenewal },
        { key: 'low', label: 'Sắp hết buổi', count: data.filter(d => deriveQuotaStatus(d) === 'Sắp hết buổi').length },
        { key: 'trial', label: 'Học thử', count: countTrial },
        { key: 'settled', label: 'Đã đủ', count: data.filter(d => (d.total_price || 0) > 0 && (d.still_owe || 0) <= 0).length },
    ];

    const openDrawer = (row) => setSelectedRow(row);

    const columns = [
        {
            title: 'Họ và tên',
            key: 'name',
            width: isMobile ? 120 : 140,
            fixed: 'left',
            render: (_, row) => (
                <div style={{ minWidth: 0 }}>
                    <button type="button" className="tt-student-btn" onClick={e => { e.stopPropagation(); openDrawer(row); }}>
                        {row.student_ref?.name}
                    </button>
                    <div className="tt-stu-c">{row.student_ref?.student_id || '—'}</div>
                </div>
            ),
            sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
            sorter: (a, b) => {
                const last = name => normalizeVietnamese((name || '').trim().split(' ').pop());
                return last(a.student_ref?.name) < last(b.student_ref?.name) ? -1 : last(a.student_ref?.name) > last(b.student_ref?.name) ? 1 : 0;
            },
        },
        {
            title: 'Tình trạng buổi',
            key: 'status',
            width: 120,
            align: 'center',
            render: (_, row) => {
                const status = deriveQuotaStatus(row);
                return <StatusTag tone={QUOTA_STATUS_TONE[status]}>{status}</StatusTag>;
            },
            filters: Object.keys(QUOTA_STATUS_TONE).map(status => ({ text: status, value: status })),
            onFilter: (value, row) => deriveQuotaStatus(row) === value,
        },
        {
            title: 'Tổng đã nộp',
            key: 'total_paid',
            width: 110,
            align: 'right',
            render: (_, row) => (
                row.total_paid > 0
                    ? <Text strong style={{ color: 'var(--ok)' }}>{formatMoney(row.total_paid)}</Text>
                    : <Text type="secondary">—</Text>
            ),
            sorter: (a, b) => (a.total_paid || 0) - (b.total_paid || 0),
        },
        {
            title: 'Còn nợ',
            key: 'still_owe',
            width: 120,
            align: 'right',
            render: (_, row) => {
                const owe = row.still_owe || 0;
                if (owe > 0) return <Text strong style={{ color: 'var(--bad)' }}>{formatMoney(owe)}</Text>;
                if (owe < 0) return <Text style={{ color: 'var(--ok)' }}>Dư {formatMoney(-owe)}</Text>;
                // owe === 0: nothing owed. Show a dash if they never had a priced package.
                return (row.total_price || 0) > 0
                    ? <Text style={{ color: 'var(--ok)' }}>Đã đủ</Text>
                    : <Text type="secondary">—</Text>;
            },
            sorter: (a, b) => (a.still_owe || 0) - (b.still_owe || 0),
        },
        {
            title: 'Buổi học còn lại',
            key: 'lessons',
            width: 130,
            align: 'center',
            render: (_, row) => {
                if (!row.total_quota) return <Text type="secondary">—</Text>;
                const rem = row.lessons_remaining;
                const color = rem <= 0 ? 'var(--bad)' : rem <= 10 ? 'var(--warn)' : 'var(--ok)';
                return (
                    <Space size={4}>
                        <Text strong style={{ color }}>{Math.max(0, rem)}</Text>
                        <Text type="secondary">/ {row.total_quota}</Text>
                        {row.warning && <StatusTag tone="warn">Sắp hết</StatusTag>}
                    </Space>
                );
            },
            sorter: (a, b) => (a.lessons_remaining ?? Infinity) - (b.lessons_remaining ?? Infinity),
        },
        {
            title: 'Số lần nộp',
            key: 'count',
            width: 75,
            align: 'center',
            render: (_, row) => row.record_count || <Text type="secondary">0</Text>,
        },
        {
            title: 'Nhắc học phí',
            key: 'notify',
            width: 120,
            align: 'center',
            fixed: isMobile ? undefined : 'right',
            render: (_, row) => {
                if (!row.total_quota) return <Text type="secondary">—</Text>;
                const sid = row.student_ref?.student_id;
                return (
                    <span onClick={e => e.stopPropagation()}>
                    <Popconfirm
                        title="Gửi thông báo nhắc học phí cho học sinh này?"
                        okText="Gửi" cancelText="Hủy"
                        onConfirm={() => sendTuitionReminder(row)}
                    >
                        <Button
                            size="small"
                            icon={<BellOutlined />}
                            danger={row.warning}
                            type={row.warning ? 'primary' : 'default'}
                            loading={remindingId === sid}
                            onClick={e => e.stopPropagation()}
                        >
                            Nhắc
                        </Button>
                    </Popconfirm>
                    </span>
                );
            },
        },
        {
            title: '',
            key: 'detail',
            width: 92,
            align: 'center',
            fixed: isMobile ? undefined : 'right',
            render: (_, row) => (
                <Button size="small" icon={<IdcardOutlined />} onClick={e => { e.stopPropagation(); openDrawer(row); }}>
                    Chi tiết
                </Button>
            ),
        },
    ];

    return (
        <div className="att2-page">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Học phí</h1>
                    <div className="att2-sub">{filteredData.length}/{data.length} học sinh</div>
                </div>
                <div className="att2-tools">
                    <Input
                        className="tt-search"
                        prefix={<SearchOutlined style={{ color: 'var(--ink-4)' }} />}
                        placeholder="Tìm tên hoặc mã..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        allowClear
                    />
                </div>
            </div>

            <div className="stat-strip">
                <span className="stat"><span className="l">Còn nợ</span><span className="v tone-bad">{countOwed}</span></span>
                <span className="stat"><span className="l">Cần gia hạn</span><span className="v tone-warn">{countRenewal}</span></span>
                <span className="stat"><span className="l">Tổng thu</span><span className="v tone-accent">{formatMoney(totalPaid)}</span></span>
                {totalOwed > 0 && (
                    <span className="stat"><span className="l">Tổng còn nợ</span><span className="v tone-bad">{formatMoney(totalOwed)}</span></span>
                )}
            </div>

            <div className="tt-filterbar">
                <div className="seg tt-filterseg" role="tablist" aria-label="Lọc học phí">
                    {quickFilters.map(f => (
                        <button key={f.key} type="button" className={filterMode === f.key ? 'on' : ''} onClick={() => setFilterMode(f.key)}>
                            {f.label} <span className="seg-n">{f.count}</span>
                        </button>
                    ))}
                </div>
            </div>

            <Table
                columns={columns}
                dataSource={filteredData.map((d, i) => ({ ...d, key: d.student_ref?._id || i }))}
                loading={loading}
                pagination={{ ...pagination, showSizeChanger: true, pageSizeOptions: ['10','20','50'], showTotal: total => `${total} học sinh` }}
                size="middle"
                onChange={(page, __, sorter) => {
                    setPagination({ current: page.current, pageSize: page.pageSize });
                    const s = Array.isArray(sorter) ? sorter[0] : sorter;
                    setSortedInfo(s?.order ? s : {});
                }}
                scroll={{ x: 'max-content' }}
                onRow={row => ({ onClick: () => openDrawer(row) })}
                locale={{ emptyText: 'Chưa có dữ liệu học phí' }}
            />
            <TuitionDetailDrawer
                visible={!!selectedRow}
                studentRow={selectedRow}
                onClose={() => setSelectedRow(null)}
                onRefresh={loadData}
                isMobile={isMobile}
            />
        </div>
    );
}
